'use client';

import Cookies from 'js-cookie';
import { Trans } from 'react-i18next';
import { useState, useEffect } from 'react';

import { useTranslate } from '@/core/locales';
import { TRANSLATION_NAMESPACES } from '@/core/constants';

import { Box, Button, Typography } from '@/components/ui';

export default function CookieBanner() {
  const { t } = useTranslate(TRANSLATION_NAMESPACES.COMMON);
  const [accepted, setAccepted] = useState(false);

  const METRIKA_ID = Number(process.env.NEXT_PUBLIC_METRIKA_ID);
  const METRIKA_URL = process.env.NEXT_PUBLIC_METRIKA_URL ?? 'https://mc.yandex.ru/metrika/tag.js';

  const loadYandexMetrika = () => {
    if (document.getElementById('yandex-metrika')) return;

    const script = document.createElement('script');
    script.id = 'yandex-metrika';
    script.type = 'text/javascript';
    script.text = `
      (function(m,e,t,r,i,k,a){
        m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
        m[i].l=1*new Date();
        for (var j = 0; j < document.scripts.length; j++) {if (document.scripts[j].src === r) { return; }}
        k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)
      })(window, document,'script','${METRIKA_URL}','ym');
      ym(${METRIKA_ID},'init',{
        webvisor: true,
        clickmap: true,
        referrer: document.referrer,
        url: location.href,
        accurateTrackBounce: true,
        trackLinks: true
      });
    `;
    document.body.appendChild(script);

    const noscript = document.createElement('noscript');
    const div = document.createElement('div');
    const img = document.createElement('img');
    img.src = `https://mc.yandex.ru/watch/${METRIKA_ID}`;
    img.style.cssText = 'position:absolute; left:-9999px;';
    img.alt = '';
    div.appendChild(img);
    noscript.appendChild(div);
    document.body.appendChild(noscript);
  };

  useEffect(() => {
    const saved = Cookies.get('cookieAccepted');
    if (saved) {
      setAccepted(true);
      loadYandexMetrika();
    }
  }, []);

  const handleAccept = () => {
    setAccepted(true);
    Cookies.set('cookieAccepted', 'true', { expires: 365, path: '/' });
    loadYandexMetrika();
  };

  if (accepted) return null;

  return (
    <Box
      className="
      fixed bottom-4 left-1/2 -translate-x-1/2
      w-[92%] sm:w-[85%] md:w-[82%]
      z-[1400]

      px-4 sm:px-6 md:px-8
      py-4 sm:py-5

      flex flex-col sm:flex-row
      items-start sm:items-center
      justify-between
      gap-4 sm:gap-6

      rounded-2xl
      bg-white
      shadow-[var(--shadow-box)]
      border border-[var(--color-grey-300)]
    "
    >
      <Box className="flex items-start sm:items-center gap-3 sm:gap-4">
        <Box className="text-2xl sm:text-3xl">🍪</Box>

        <Box>
          <Typography className="text-sm sm:text-base font-bold text-[var(--color-grey-800)] leading-tight block">
            {t('cookieBanner.title')}
          </Typography>

          <Typography className="text-xs sm:text-sm text-[var(--color-grey-500)] leading-snug mt-1 max-w-xl">
            <Trans
              i18nKey="cookieBanner.description"
              components={{
                policy: (
                  <a
                    href="/privacy-policy/"
                    target="_blank"
                    className="text-[var(--color-primary)] underline underline-offset-2 hover:text-[var(--color-primary-light)] transition-colors"
                  />
                ),
              }}
            />
          </Typography>
        </Box>
      </Box>

      <Button
        onClick={handleAccept}
        className="
        w-full sm:w-auto

        text-sm sm:text-base
        text-[var(--color-primary-contrast)]
        font-semibold

        px-5 sm:px-7
        py-2.5 sm:py-3

        rounded-xl
        bg-gradient-to-r from-[var(--color-primary-light)] to-[var(--color-info)]
        hover:opacity-90
        active:scale-95
        transition-all duration-150

        cursor-pointer
        normal-case
        shadow-[0_4px_14px_rgba(131,222,166,0.35)]
      "
      >
        {t('cookieBanner.accept')}
      </Button>
    </Box>
  );
}
